import { Injectable, NgZone } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  distinctUntilChanged,
  map,
} from 'rxjs';
import { Participant, Room, RoomEvent, Track, TrackPublication } from 'livekit-client';
import { AGENT_USER_IDS } from '../sessions/session/items-canvas/board-items/agent-ids';
import { LunaIdleCountdown } from '../models/luna-idle-countdown';
import { LunaCreation } from '../models/luna-creation';
import { parseLunaCreations } from './luna-workspace/luna-creations.util';
import { LunaLocation, parseLunaLocation } from './luna-workspace/luna-location.util';

/**
 * LiveKit participant attribute under which the Agents SDK (RoomIO) automatically
 * publishes the tutor's realtime state. Values come from the agent lifecycle:
 * `initializing | idle | listening | thinking | speaking`. No worker change is
 * needed — the FE only reads + subscribes.
 * Docs: https://docs.livekit.io/agents/build/state/
 */
export const ATTRIBUTE_AGENT_STATE = 'lk.agent.state';

/**
 * LiveKit participant attribute the worker publishes (via `set_attributes`) when
 * Luna is paused/resumed from the pause button. String `"true"`/`"false"`. It is
 * MERGED alongside `lk.agent.state` (worker: `_handle_pause`/`_handle_resume`) so
 * the FE can hard-override the tile to a "Paused" state.
 */
export const ATTRIBUTE_LUNA_PAUSED = 'luna.paused';

/**
 * LiveKit participant attribute the worker publishes (via `set_attributes`) for
 * Practice Roleplay personas: their real, persistent agent userId (see
 * docs/superpowers/specs/2026-08-27-roleplay-identity-vct-diarization-design.md).
 * Registered into {@link AGENT_USER_IDS} on sight so cursor/board-object attribution
 * recognizes the persona as an agent, same as the real AI tutor's fixed id.
 */
export const ATTRIBUTE_AGENT_USER_ID = 'agent.userId';

/**
 * LiveKit participant attribute the worker publishes (via `set_attributes`) with a
 * JSON-encoded {@link LunaFocus} describing what region of the board Luna is
 * currently focused on (a section it just laid out, or where it's writing). It is
 * MERGED alongside `lk.agent.state`, so the FE reads it off the same agent
 * participant + `attributesChanged` subscription. Bad/missing JSON is ignored.
 */
export const ATTRIBUTE_LUNA_FOCUS = 'luna.focus';

/**
 * LiveKit participant attribute the AI-tutor worker publishes (via `set_attributes`) with a
 * JSON-encoded {@link LunaIdleCountdown} while the student's silence clock is running
 * (feature F3). MERGED alongside `lk.agent.state`, so the FE reads it off the same agent
 * participant + `attributesChanged` subscription. `{active:false}`, bad, or missing JSON
 * maps to `null` (no ring), so a stale attribute can never leave a ring on screen.
 */
export const ATTRIBUTE_LUNA_IDLE_COUNTDOWN = 'luna.idle_countdown';

/**
 * LiveKit participant attribute the AI-tutor worker publishes with every visual and app Luna
 * is making or has made (`{v: 1, items: [...]}`, see {@link LunaCreation}). Republished on
 * each step, finish and placement. Bad or missing JSON is an empty list.
 */
export const ATTRIBUTE_LUNA_CREATIONS = 'luna.creations';

/**
 * LiveKit RPC method the FE registers on its local participant (feature F2). The worker
 * INVOKES it (worker → FE) when the tutor calls the `end_session` tool, passing a
 * JSON-encoded structured session-end report whose shape is defined by the partner's
 * `endSessionConfig`. RPC (not a `luna.*` attribute) because session-end is a ONE-SHOT
 * EVENT: the handler acks so the worker confirms delivery before teardown, and nothing is
 * retained to re-fire on reconnect. The payload is surfaced as a plain `Subject` and
 * delivered to the embedding parent container via `postMessage`.
 */
export const RPC_DELIVER_SESSION_END = 'deliverSessionEnd';

/**
 * LiveKit attribute marking a participant as publishing ON BEHALF of another — the Tavus
 * avatar DELEGATE carries it. The delegate is `isAgent` too but publishes no presence
 * attributes, so every presence read must skip it and use the PRINCIPAL: reading state off
 * the delegate yields `initializing` forever (see {@link isTutorPrincipal}).
 */
export const ATTRIBUTE_PUBLISH_ON_BEHALF = 'lk.publish_on_behalf';

/**
 * Whether this participant is the tutor PRINCIPAL — the one that publishes
 * `lk.agent.state` / `luna.*`. Excludes the Tavus avatar delegate, which is agent-kind but
 * carries no presence attributes.
 */
function isTutorPrincipal(participant: Participant): boolean {
  return participant.isAgent && !participant.attributes?.[ATTRIBUTE_PUBLISH_ON_BEHALF];
}

/**
 * What Luna is currently looking at on the board — the payload of the
 * {@link ATTRIBUTE_LUNA_FOCUS} attribute (a JSON string on the wire). `bbox` is in
 * BOARD coordinates (not screen), so the FE can frame the viewport onto it.
 */
export interface LunaFocus {
  /** The region of interest, in BOARD coordinates. */
  bbox: { x: number; y: number; width: number; height: number };
  /**
   * Why Luna moved focus — drives how aggressively the viewport re-frames. 'drawing' is a figure
   * she just drew: the view fits all of it.
   */
  reason: 'section-start' | 'writing' | 'drawing';
  /** Optional human-readable label for the focused region (e.g. a section title). */
  title?: string;
  /** Worker-side timestamp (epoch ms) the focus was published. */
  ts: number;
}

/**
 * Luna's realtime presence state. The five inner values are published by the
 * agent worker; `disconnected` is a FE-only default for "no tutor in the call"
 * (before connect / after the agent leaves / on room teardown).
 */
export type AgentState =
  | 'initializing'
  | 'idle'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'disconnected';

/**
 * The DISPLAY state shown on Luna's tile — the raw {@link AgentState} plus the
 * FE-only `paused` state, derived from the agent state, the `luna.paused` worker
 * attribute, and the LOCAL student's mic-mute (see {@link toDisplayState}).
 */
export type DisplayState = AgentState | 'paused';

/** The agent-published states we accept off the wire (everything else is ignored). */
const KNOWN_AGENT_STATES: ReadonlySet<AgentState> = new Set<AgentState>([
  'initializing',
  'idle',
  'listening',
  'thinking',
  'speaking',
]);

/**
 * Fold the three presence inputs into the single tile DISPLAY state. Precedence
 * (order matters — first match wins):
 *  1. agent disconnected           → `disconnected`
 *  2. paused (hard override)       → `paused`  (pause mutes Luna both ways)
 *  3. agent thinking/speaking      → as-is     (Luna is actively doing something;
 *                                                local mute is irrelevant here)
 *  4. local mic muted              → `idle`    (Luna can't hear the student, so
 *                                                don't misleadingly show "Listening")
 *  5. otherwise                    → the raw agent state
 */
export function toDisplayState(
  agentState: AgentState,
  paused: boolean,
  localMicMuted: boolean,
): DisplayState {
  if (agentState === 'disconnected') {
    return 'disconnected';
  }
  if (paused) {
    return 'paused';
  }
  if (agentState === 'thinking' || agentState === 'speaking') {
    return agentState;
  }
  if (localMicMuted) {
    return 'idle';
  }
  return agentState;
}

/**
 * Luna presence spine.
 *
 * Subscribes ONCE to the (singleton) LiveKit Room and derives the tutor's
 * realtime state from the agent participant's `lk.agent.state` attribute. It is
 * attached to the room by LivekitService (same pattern as
 * {@link LivekitAgentChatService}) so it reuses the existing call connection and
 * never opens a second Room.
 *
 * State resolution:
 *  - INITIAL read: on attach (or when the agent joins) we read whatever
 *    `lk.agent.state` is already on the agent participant.
 *  - CHANGE events: `RoomEvent.ParticipantAttributesChanged` fires with the
 *    changed attributes whenever the worker updates its state — we push the new
 *    value when the change includes `lk.agent.state`.
 *  - The agent (dis)connecting and the room tearing down reset the stream to
 *    `disconnected`.
 *
 * Structured so a sibling `lunaFocus$` (what Luna is looking at, from other
 * agent attributes) can be derived off the same single subscription later —
 * only `agentState$` is implemented here.
 */
@Injectable({ providedIn: 'root' })
export class LunaPresenceService {
  private room?: Room;

  private readonly agentStateSubject = new BehaviorSubject<AgentState>('disconnected');

  private readonly confirmedPausedSubject = new BehaviorSubject<boolean>(false);

  private readonly optimisticPausedSubject = new BehaviorSubject<boolean | null>(null);

  private readonly localMicMutedSubject = new BehaviorSubject<boolean>(false);

  private readonly lunaFocusSubject = new BehaviorSubject<LunaFocus | null>(null);
  private readonly creationsSubject = new BehaviorSubject<LunaCreation[]>([]);

  private readonly lunaLocationSubject = new BehaviorSubject<LunaLocation | null>(null);

  private readonly idleCountdownSubject = new BehaviorSubject<LunaIdleCountdown | null>(null);

  /**
   * One-shot end-of-session report (parsed {@link RPC_DELIVER_SESSION_END}).
   * A plain Subject, not a BehaviorSubject: it carries no "current" value — each
   * emission is a discrete "the session just ended, here's the payload" event that a
   * consumer (AiTutorService) forwards to the iframe host via postMessage.
   */
  private readonly endSessionObjectsSubject = new Subject<Record<string, unknown>>();

  /** Luna's current realtime state — bind with the async pipe (OnPush safe). */
  readonly agentState$: Observable<AgentState> = this.agentStateSubject.pipe(
    distinctUntilChanged(),
  );

  /**
   * Whether Luna is paused. A local override makes orb taps feel immediate while
   * the worker RPC is in flight; the next matching worker attribute clears it.
   */
  readonly paused$: Observable<boolean> = combineLatest([
    this.confirmedPausedSubject,
    this.optimisticPausedSubject,
  ]).pipe(
    map(([confirmed, optimistic]) => optimistic ?? confirmed),
    distinctUntilChanged(),
  );

  /** Whether the LOCAL student's microphone is muted (Luna can't hear them). */
  readonly localMicMuted$: Observable<boolean> = this.localMicMutedSubject.pipe(
    distinctUntilChanged(),
  );

  /**
   * What Luna is currently focused on (the parsed {@link ATTRIBUTE_LUNA_FOCUS}
   * attribute), or `null` when there's no focus / the agent is gone. Derived off
   * the SAME agent-participant `attributesChanged` subscription as
   * {@link agentState$}. Bind with the async pipe (OnPush safe).
   */
  readonly lunaFocus$: Observable<LunaFocus | null> = this.lunaFocusSubject.pipe(
    distinctUntilChanged(),
  );

  /**
   * Where Luna is working: the whiteboard or an app (by item uid), from the `target` the
   * agent puts on {@link ATTRIBUTE_LUNA_FOCUS}; null until she has been anywhere / is gone.
   */
  readonly lunaLocation$: Observable<LunaLocation | null> = this.lunaLocationSubject.pipe(
    distinctUntilChanged(
      (a, b) =>
        a?.kind === b?.kind &&
        (a?.kind !== 'app' || (b?.kind === 'app' && a.itemUid === b.itemUid)),
    ),
  );

  /**
   * The running student-silence countdown (parsed {@link ATTRIBUTE_LUNA_IDLE_COUNTDOWN}),
   * or `null` when no countdown is active / the agent is gone. Derived off the SAME
   * agent-participant `attributesChanged` subscription as {@link agentState$}.
   * `distinctUntilChanged` on `endsAt` so a re-published identical deadline is a no-op.
   * Bind with the async pipe (OnPush safe).
   */
  readonly idleCountdown$: Observable<LunaIdleCountdown | null> = this.idleCountdownSubject.pipe(
    distinctUntilChanged((a, b) => (a?.endsAt ?? null) === (b?.endsAt ?? null)),
  );

  /** What Luna is making or has made (the parsed {@link ATTRIBUTE_LUNA_CREATIONS}). */
  readonly creations$: Observable<LunaCreation[]> = this.creationsSubject.asObservable();

  /**
   * Emits the structured session-end report once, when the worker delivers it via the
   * {@link RPC_DELIVER_SESSION_END} RPC. AiTutorService subscribes and
   * postMessages it to the embedding parent container.
   */
  readonly endSessionObjects$: Observable<Record<string, unknown>> =
    this.endSessionObjectsSubject.asObservable();

  /**
   * The single DISPLAY state for Luna's tile, folding the agent state, the paused
   * attribute, and the local mic-mute via {@link toDisplayState}. Centralized here
   * so every surface (tile indicator + chat pill) shares one source of truth and
   * they never disagree. Bind with the async pipe (OnPush safe).
   */
  readonly displayState$: Observable<DisplayState> = combineLatest([
    this.agentState$,
    this.paused$,
    this.localMicMuted$,
  ]).pipe(
    map(([agentState, paused, micMuted]) => toDisplayState(agentState, paused, micMuted)),
    distinctUntilChanged(),
  );

  constructor(private readonly zone: NgZone) {}

  /** Show a pause/resume request immediately while LiveKit confirms it. */
  setPausedOptimistically(paused: boolean): void {
    if (this.optimisticPausedSubject.value === paused) {
      return;
    }
    this.zone.run(() => this.optimisticPausedSubject.next(paused));
  }

  /** Roll back a pending pause/resume request after an RPC failure. */
  clearOptimisticPaused(): void {
    if (this.optimisticPausedSubject.value === null) {
      return;
    }
    this.zone.run(() => this.optimisticPausedSubject.next(null));
  }

  /**
   * Wire this service onto the room. Idempotent per room — LivekitService calls
   * it once with the singleton Room, and the handlers persist across
   * connect/disconnect cycles (the agent join/leave events do the per-call work).
   */
  attach(room: Room): void {
    if (this.room === room) {
      return;
    }
    this.detach();
    this.room = room;
    // Worker → FE RPC: the worker invokes `deliverSessionEnd` to deliver Luna's
    // structured end-of-session report (registered on our local participant; the handler
    // acks so the worker can confirm delivery before teardown).
    room.localParticipant.registerRpcMethod(RPC_DELIVER_SESSION_END, this.handleDeliverSessionEnd);
    room
      .on(RoomEvent.ParticipantAttributesChanged, this.handleAttributesChanged)
      .on(RoomEvent.ParticipantConnected, this.handleParticipantConnected)
      .on(RoomEvent.ParticipantDisconnected, this.handleParticipantDisconnected)
      .on(RoomEvent.Disconnected, this.handleRoomDisconnected)
      // Re-seed on (re)connect. LivekitService attaches us to a BRAND-NEW, unconnected
      // Room in its constructor, so the sync below finds nothing; and a tutor already in
      // the room when we join arrives in the JOIN RESPONSE, which fires no
      // ParticipantConnected. Without this, a reload into a live call left Luna reading
      // `disconnected` until she next changed an attribute (i.e. until she spoke).
      .on(RoomEvent.Connected, this.handleRoomConnected)
      .on(RoomEvent.Reconnected, this.handleRoomConnected)
      // Local mic-mute: TrackMuted/TrackUnmuted fire for every participant, so the
      // handlers filter to the local participant's microphone source.
      .on(RoomEvent.TrackMuted, this.handleTrackMuted)
      .on(RoomEvent.TrackUnmuted, this.handleTrackUnmuted)
      // The mic can publish AFTER we attach and can publish ALREADY muted (student
      // joined muted) — in which case no TrackMuted event fires, so re-sync on
      // (un)publish. Unpublishing the mic also means Luna can't hear the student.
      .on(RoomEvent.LocalTrackPublished, this.handleLocalTrackPublished)
      .on(RoomEvent.LocalTrackUnpublished, this.handleLocalTrackUnpublished);
    // Cover the case where the agent is already in the room when we attach.
    this.syncFromExistingAgent();
    // Seed the local mic-mute from whatever's already published.
    this.syncLocalMicMuted();
  }

  /** Remove listeners and reset the stream (used when the room is torn down). */
  detach(): void {
    const room = this.room;
    if (room) {
      room
        .off(RoomEvent.ParticipantAttributesChanged, this.handleAttributesChanged)
        .off(RoomEvent.ParticipantConnected, this.handleParticipantConnected)
        .off(RoomEvent.ParticipantDisconnected, this.handleParticipantDisconnected)
        .off(RoomEvent.Disconnected, this.handleRoomDisconnected)
        .off(RoomEvent.Connected, this.handleRoomConnected)
        .off(RoomEvent.Reconnected, this.handleRoomConnected)
        .off(RoomEvent.TrackMuted, this.handleTrackMuted)
        .off(RoomEvent.TrackUnmuted, this.handleTrackUnmuted)
        .off(RoomEvent.LocalTrackPublished, this.handleLocalTrackPublished)
        .off(RoomEvent.LocalTrackUnpublished, this.handleLocalTrackUnpublished);
      room.localParticipant.unregisterRpcMethod(RPC_DELIVER_SESSION_END);
    }
    this.room = undefined;
    this.setState('disconnected');
    this.resetPaused();
    this.setLocalMicMuted(false);
    this.setLunaFocus(null);
    this.setLunaLocation(null);
    this.setIdleCountdown(null);
    this.setCreations([]);
  }

  /** An attribute update — push whichever presence attributes this agent carries. */
  private readonly handleAttributesChanged = (
    changed: Record<string, string>,
    participant: Participant,
  ): void => {
    if (!isTutorPrincipal(participant)) {
      return;
    }
    if (ATTRIBUTE_AGENT_STATE in changed) {
      this.setState(this.parseAgentState(changed[ATTRIBUTE_AGENT_STATE]));
    }
    if (ATTRIBUTE_LUNA_PAUSED in changed) {
      this.setPaused(this.parsePaused(changed[ATTRIBUTE_LUNA_PAUSED]));
    }
    if (ATTRIBUTE_AGENT_USER_ID in changed && changed[ATTRIBUTE_AGENT_USER_ID]) {
      AGENT_USER_IDS.add(changed[ATTRIBUTE_AGENT_USER_ID]);
    }
    if (ATTRIBUTE_LUNA_FOCUS in changed) {
      // Bad/missing JSON → parseLunaFocus returns null; keep the last good focus
      // rather than clobbering it on a transient bad payload.
      const focus = this.parseLunaFocus(changed[ATTRIBUTE_LUNA_FOCUS]);
      if (focus) {
        this.setLunaFocus(focus);
      }
      const location = parseLunaLocation(changed[ATTRIBUTE_LUNA_FOCUS]);
      if (location) {
        this.setLunaLocation(location);
      }
    }
    if (ATTRIBUTE_LUNA_CREATIONS in changed) {
      this.setCreations(parseLunaCreations(changed[ATTRIBUTE_LUNA_CREATIONS]));
    }
    if (ATTRIBUTE_LUNA_IDLE_COUNTDOWN in changed) {
      // Unlike focus, ALWAYS apply the parsed value including `null`: `{active:false}`
      // (or bad JSON) must clear the ring, never leave a stale countdown on screen.
      this.setIdleCountdown(this.parseIdleCountdown(changed[ATTRIBUTE_LUNA_IDLE_COUNTDOWN]));
    }
  };

  /**
   * `deliverSessionEnd` RPC handler (worker → FE). The worker invokes this when Luna
   * calls the `end_session` tool; we parse the payload, emit it on {@link endSessionObjects$}
   * (AiTutorService forwards it to the iframe host), and ACK so the worker knows it landed
   * before the room tears down. RPC (not an attribute) because session-end is a one-shot
   * EVENT: nothing is retained to re-fire on reconnect. Bad JSON → rejected so the worker
   * sees the failure.
   */
  private readonly handleDeliverSessionEnd = async (data: { payload: string }): Promise<string> => {
    const summary = this.parseEndSessionObjects(data.payload);
    if (!summary) {
      throw new Error('deliverSessionEnd: payload was not valid JSON');
    }
    this.zone.run(() => this.endSessionObjectsSubject.next(summary));
    return JSON.stringify({ ok: true });
  };

  /** Agent joined — read whatever state it already advertises. Skips the Tavus delegate,
   * which joins AFTER the principal and would otherwise reset us to `initializing`. */
  private readonly handleParticipantConnected = (participant: Participant): void => {
    if (isTutorPrincipal(participant)) {
      this.setState(this.readAgentState(participant));
      this.setPaused(this.readPaused(participant));
      this.setLunaFocus(this.readLunaFocus(participant));
      this.setLunaLocation(parseLunaLocation(participant.attributes?.[ATTRIBUTE_LUNA_FOCUS]));
      this.setCreations(parseLunaCreations(participant.attributes?.[ATTRIBUTE_LUNA_CREATIONS]));
      this.setIdleCountdown(this.readIdleCountdown(participant));
      const agentUserId = participant.attributes?.[ATTRIBUTE_AGENT_USER_ID];
      if (agentUserId) {
        AGENT_USER_IDS.add(agentUserId);
      }
    }
  };

  /** Agent left — back to disconnected, and clear the paused/focus overrides. The Tavus
   * delegate can end its own session while Luna stays on the call, so only the principal
   * leaving counts as the tutor leaving. */
  private readonly handleParticipantDisconnected = (participant: Participant): void => {
    if (isTutorPrincipal(participant)) {
      this.setState('disconnected');
      this.resetPaused();
      this.setLunaFocus(null);
      this.setLunaLocation(null);
      this.setIdleCountdown(null);
      this.setCreations([]);
    }
  };

  /** Call connected (or fully reconnected) — seed from whoever is already in the room. */
  private readonly handleRoomConnected = (): void => {
    this.syncFromExistingAgent();
    this.syncLocalMicMuted();
  };

  /** Room torn down (call ended) — reset. */
  private readonly handleRoomDisconnected = (): void => {
    this.setState('disconnected');
    this.resetPaused();
    this.setLunaFocus(null);
    this.setLunaLocation(null);
    this.setIdleCountdown(null);
    this.setCreations([]);
  };

  /** A track was muted — reflect it if it's the local participant's microphone. */
  private readonly handleTrackMuted = (
    publication: TrackPublication,
    participant: Participant,
  ): void => {
    if (this.isLocalMicPublication(publication, participant)) {
      this.setLocalMicMuted(true);
    }
  };

  /** A track was unmuted — reflect it if it's the local participant's microphone. */
  private readonly handleTrackUnmuted = (
    publication: TrackPublication,
    participant: Participant,
  ): void => {
    if (this.isLocalMicPublication(publication, participant)) {
      this.setLocalMicMuted(false);
    }
  };

  /** The local mic (re)published — re-read its muted state (it may publish already
   *  muted, in which case no TrackMuted event fires). */
  private readonly handleLocalTrackPublished = (publication: TrackPublication): void => {
    if (publication.source === Track.Source.Microphone) {
      this.setLocalMicMuted(publication.isMuted);
    }
  };

  /** The local mic was unpublished — Luna can no longer hear the student. */
  private readonly handleLocalTrackUnpublished = (publication: TrackPublication): void => {
    if (publication.source === Track.Source.Microphone) {
      this.setLocalMicMuted(true);
    }
  };

  /** True when a track event is for the local participant's microphone. */
  private isLocalMicPublication(publication: TrackPublication, participant: Participant): boolean {
    return participant.isLocal && publication.source === Track.Source.Microphone;
  }

  /** Seed the stream from an agent that's already present in the room. */
  private syncFromExistingAgent(): void {
    const room = this.room;
    if (!room) {
      return;
    }
    for (const participant of room.remoteParticipants.values()) {
      // Only the PRINCIPAL carries presence attributes. The map is unordered, so without
      // this filter the Tavus delegate could win and pin the state to `initializing`.
      if (isTutorPrincipal(participant)) {
        this.setState(this.readAgentState(participant));
        this.setPaused(this.readPaused(participant));
        this.setLunaFocus(this.readLunaFocus(participant));
        this.setLunaLocation(parseLunaLocation(participant.attributes?.[ATTRIBUTE_LUNA_FOCUS]));
        this.setCreations(parseLunaCreations(participant.attributes?.[ATTRIBUTE_LUNA_CREATIONS]));
        // Same registration as handleParticipantConnected/handleAttributesChanged —
        // needed here too since a tutor who joins/reconnects after the agent is
        // already present never gets ParticipantConnected or a later attribute change
        // for it.
        const agentUserId = participant.attributes?.[ATTRIBUTE_AGENT_USER_ID];
        if (agentUserId) {
          AGENT_USER_IDS.add(agentUserId);
        }
        return;
      }
    }
  }

  /** Seed the local mic-mute from the current microphone publication (if any). */
  private syncLocalMicMuted(): void {
    const room = this.room;
    if (!room) {
      return;
    }
    const micPublication = room.localParticipant.getTrackPublication(Track.Source.Microphone);
    // No published mic yet → treat as not muted so we don't hide "Listening"
    // before the student has even joined audio.
    this.setLocalMicMuted(micPublication?.isMuted ?? false);
  }

  /** Read the agent-state attribute off a participant (defaults to initializing). */
  private readAgentState(participant: Participant): AgentState {
    return this.parseAgentState(participant.attributes?.[ATTRIBUTE_AGENT_STATE]);
  }

  /** Read the paused attribute off a participant (defaults to not-paused). */
  private readPaused(participant: Participant): boolean {
    return this.parsePaused(participant.attributes?.[ATTRIBUTE_LUNA_PAUSED]);
  }

  /** Coerce a raw attribute value to a known AgentState (fallback: initializing). */
  private parseAgentState(value: string | undefined): AgentState {
    return value && KNOWN_AGENT_STATES.has(value as AgentState)
      ? (value as AgentState)
      : 'initializing';
  }

  /** Coerce the `luna.paused` string attribute to a boolean (only "true" is paused). */
  private parsePaused(value: string | undefined): boolean {
    return value === 'true';
  }

  /** Read the focus attribute off a participant (null when absent/invalid). */
  private readLunaFocus(participant: Participant): LunaFocus | null {
    return this.parseLunaFocus(participant.attributes?.[ATTRIBUTE_LUNA_FOCUS]);
  }

  /**
   * Parse the JSON-encoded `luna.focus` attribute into a {@link LunaFocus}.
   * Any parse error or shape mismatch returns `null` (caller decides whether to
   * keep the last value or reset).
   */
  private parseLunaFocus(value: string | undefined): LunaFocus | null {
    if (!value) {
      return null;
    }
    try {
      const parsed = JSON.parse(value) as Partial<LunaFocus>;
      const bbox = parsed?.bbox;
      if (
        !bbox ||
        typeof bbox.x !== 'number' ||
        typeof bbox.y !== 'number' ||
        typeof bbox.width !== 'number' ||
        typeof bbox.height !== 'number' ||
        (parsed.reason !== 'section-start' &&
          parsed.reason !== 'writing' &&
          parsed.reason !== 'drawing')
      ) {
        return null;
      }
      return {
        bbox: { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height },
        reason: parsed.reason,
        title: typeof parsed.title === 'string' ? parsed.title : undefined,
        ts: typeof parsed.ts === 'number' ? parsed.ts : Date.now(),
      };
    } catch {
      return null;
    }
  }

  /**
   * Parse the JSON-encoded `deliverSessionEnd` RPC payload into a plain object. The
   * payload shape is partner-defined (endSessionConfig), so we only require that it
   * parses to a non-null object; any error returns `null` (the emission is skipped).
   */
  private parseEndSessionObjects(value: string | undefined): Record<string, unknown> | null {
    if (!value) {
      return null;
    }
    try {
      const parsed = JSON.parse(value) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  /**
   * Push a new state. Room events fire outside Angular's zone
   * (LivekitService creates the Room in `runOutsideAngular`), so re-enter the
   * zone to keep OnPush async-pipe consumers rendering.
   */
  private setState(state: AgentState): void {
    if (this.agentStateSubject.value === state) {
      return;
    }
    this.zone.run(() => this.agentStateSubject.next(state));
  }

  /**
   * Push a worker-confirmed paused value and release a matching optimistic override.
   */
  private setPaused(paused: boolean): void {
    const confirmedChanged = this.confirmedPausedSubject.value !== paused;
    const optimisticConfirmed = this.optimisticPausedSubject.value === paused;
    if (!confirmedChanged && !optimisticConfirmed) {
      return;
    }
    this.zone.run(() => {
      if (confirmedChanged) {
        this.confirmedPausedSubject.next(paused);
      }
      if (optimisticConfirmed) {
        this.optimisticPausedSubject.next(null);
      }
    });
  }

  /** Clear worker and optimistic pause state when the agent or room leaves. */
  private resetPaused(): void {
    const confirmedChanged = this.confirmedPausedSubject.value;
    const hasOptimisticState = this.optimisticPausedSubject.value !== null;
    if (!confirmedChanged && !hasOptimisticState) {
      return;
    }
    this.zone.run(() => {
      if (confirmedChanged) {
        this.confirmedPausedSubject.next(false);
      }
      if (hasOptimisticState) {
        this.optimisticPausedSubject.next(null);
      }
    });
  }

  /** Push a new mic-mute value, re-entering Angular's zone (see {@link setState}). */
  private setLocalMicMuted(muted: boolean): void {
    if (this.localMicMutedSubject.value === muted) {
      return;
    }
    this.zone.run(() => this.localMicMutedSubject.next(muted));
  }

  /** Push a new location, re-entering Angular's zone (see {@link setState}). */
  private setLunaLocation(location: LunaLocation | null): void {
    this.zone.run(() => this.lunaLocationSubject.next(location));
  }

  /** Push a new focus value, re-entering Angular's zone (see {@link setState}). */
  private setLunaFocus(focus: LunaFocus | null): void {
    this.zone.run(() => this.lunaFocusSubject.next(focus));
  }

  /** Read the idle-countdown attribute off a participant (null when absent/inactive). */
  private readIdleCountdown(participant: Participant): LunaIdleCountdown | null {
    return this.parseIdleCountdown(participant.attributes?.[ATTRIBUTE_LUNA_IDLE_COUNTDOWN]);
  }

  /**
   * Parse the JSON-encoded `luna.idle_countdown` attribute into a
   * {@link LunaIdleCountdown}. Returns `null` for the inactive payload (`{active:false}`),
   * any parse error, or a shape mismatch — so the caller always clears the ring.
   */
  private parseIdleCountdown(value: string | undefined): LunaIdleCountdown | null {
    if (!value) {
      return null;
    }
    try {
      const parsed = JSON.parse(value) as Partial<LunaIdleCountdown> & {
        active?: boolean;
      };
      if (
        parsed?.active !== true ||
        typeof parsed.endsAt !== 'number' ||
        typeof parsed.total !== 'number'
      ) {
        return null;
      }
      return {
        endsAt: parsed.endsAt,
        total: parsed.total,
        tier: typeof parsed.tier === 'number' ? parsed.tier : 1,
        label: 'your_turn',
      };
    } catch {
      return null;
    }
  }

  /** Push the creations list, re-entering Angular's zone (see {@link setState}). */
  private setCreations(creations: LunaCreation[]): void {
    this.zone.run(() => this.creationsSubject.next(creations));
  }

  /** Push a new countdown value, re-entering Angular's zone (see {@link setState}). */
  private setIdleCountdown(countdown: LunaIdleCountdown | null): void {
    this.zone.run(() => this.idleCountdownSubject.next(countdown));
  }
}
